/**
 * BlindsModalClassic — Monolithic blinds device modal template.
 *
 * Complete blinds control panel in a single file:
 * - Position slider (0–100%) with current position display
 * - Preset quick-buttons: Fully Open (100%), Half (50%), Closed (0%)
 *
 * Part of the monolithic modal architecture.
 */
import { useState, useCallback, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import './BlindsModalClassic.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const BLINDS_CLASSIC_SECTIONS: ModalSectionMeta[] = [
  // ── Modal Background (header + overall) ──
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [
    { id: 'header-back-icon', label: 'Back Arrow Icon', editableProps: ['color', 'fontSize'] },
    { id: 'header-title', label: 'Device Name Text', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'header-edit-icon', label: 'Edit Icon', editableProps: ['color', 'fontSize'] },
  ]},
  // ── Position Section ──
  { id: 'position', label: 'Position', icon: 'blinds', slots: [
    { id: 'position-label', label: 'Title "POSITION"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'position-value', label: 'Position Percentage Value', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'position-unit', label: 'Unit "%"', editableProps: ['fontSize', 'color'] },
    { id: 'position-slider', label: 'Position Slider Track', editableProps: ['background', 'borderRadius', 'height'] },
    { id: 'position-slider-fill', label: 'Slider Fill (open portion)', editableProps: ['background', 'borderRadius'] },
    { id: 'position-slats', label: 'Blinds Slat Visual', editableProps: ['background', 'borderRadius', 'gap'] },
  ]},
  // ── Presets Section ──
  { id: 'presets', label: 'Presets', icon: 'tune', slots: [
    { id: 'presets-label', label: 'Title "PRESETS"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'preset-btn', label: 'Preset Button', editableProps: ['background', 'color', 'borderRadius', 'padding', 'fontSize', 'border', 'glow'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
    { id: 'preset-btn-icon', label: 'Preset Button Icon', editableProps: ['fontSize', 'color'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
    { id: 'preset-btn-text', label: 'Preset Button Text', editableProps: ['fontSize', 'color', 'fontWeight'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
]

const DEFAULT_SECTION_ORDER = BLINDS_CLASSIC_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRESETS = [
  { id: 'closed', label: 'Closed',     icon: 'expand_more', value: 0 },
  { id: 'half',   label: 'Half',       icon: 'density_medium', value: 50 },
  { id: 'open',   label: 'Fully Open', icon: 'expand_less', value: 100 },
] as const

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  const v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BlindsModalClassic({
  device,
  deviceState,
  onCommand,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
}: DeviceModalTemplateProps) {
  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- State from device ----
  const deviceOpenPercent = numAttr(deviceState, 'openPercent', 'position', 'openLevel') ?? 50
  const clampedDevicePercent = Math.max(0, Math.min(100, deviceOpenPercent))

  // ---- Local UI state (pending slider value) ----
  const [pendingPercent, setPendingPercent] = useState<number | null>(null)
  const displayPercent = pendingPercent ?? clampedDevicePercent

  // ---- Commands ----
  const setPosition = useCallback((value: number) => {
    const clamped = Math.max(0, Math.min(100, value))
    setPendingPercent(clamped)
    if (isEditorMode) return
    if (clamped === 100) {
      onCommand('open', { openPercent: 100 })
    } else if (clamped === 0) {
      onCommand('close', { openPercent: 0 })
    } else {
      onCommand('setPosition', { openPercent: clamped })
    }
  }, [isEditorMode, onCommand])

  const handleSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPendingPercent(Number(e.currentTarget.value))
  }, [])

  const handleSliderPointerUp = useCallback((e: React.PointerEvent<HTMLInputElement>) => {
    setPosition(Number(e.currentTarget.value))
  }, [setPosition])

  // Active preset: one that exactly matches the current display percent
  const activePreset = PRESETS.find((p) => p.value === displayPercent)?.id ?? null

  return (
    <div
      className={`blinds-modal-classic${isEditorMode ? ' editor-mode' : ''}`}
      data-modal-template-id="blinds-classic"
      onContextMenuCapture={handleContextMenu}
    >
      {cssOverrides && (
        <style dangerouslySetInnerHTML={{ __html: cssOverrides }} />
      )}

      {/* Header (always first, not reorderable) */}
      <div className="blinds-modal-classic__header">
        <button className="blinds-modal-classic__back" onClick={onClose} aria-label="Back">
          <span className="material-symbols-rounded" data-modal-slot="header-back-icon">arrow_back</span>
        </button>
        <h2 className="blinds-modal-classic__title" data-modal-slot="header-title">{device.name || 'Window Blinds'}</h2>
        <button
          className="blinds-modal-classic__edit"
          onClick={() => {/* future: edit nickname */}}
          aria-label="Edit"
        >
          <span className="material-symbols-rounded" data-modal-slot="header-edit-icon">edit</span>
        </button>
      </div>

      {/* Sections rendered in order */}
      {orderedSections.map((sectionId) => {
        if (!isSectionVisible(sectionId)) return null
        const hl = highlightedSection === sectionId ? ' section-highlighted' : ''
        switch (sectionId) {
          case 'position':
            return (
              <div key="position" className={`blinds-modal-classic__position-section${hl}`} data-modal-section="position">
                <div className="blinds-modal-classic__position-label" data-modal-slot="position-label">POSITION</div>

                {/* Slat visual — blinds retract upward as openPercent increases */}
                <div className="blinds-modal-classic__slat-visual" data-modal-slot="position-slats">
                  <div className="blinds-modal-classic__slat-window">
                    {/* Window background visible when blinds open */}
                    <div className="blinds-modal-classic__window-bg" />
                    {/* Slats stack upward — closedHeight = full, openHeight = collapsed at top */}
                    <div
                      className="blinds-modal-classic__slat-group"
                      style={{ height: `${100 - displayPercent}%` }}
                    >
                      {Array.from({ length: 7 }, (_, i) => (
                        <div key={`slat-${i}`} className="blinds-modal-classic__slat" />
                      ))}
                    </div>
                    {/* Percentage display inside window */}
                    <div className="blinds-modal-classic__position-display">
                      <span className="blinds-modal-classic__position-value" data-modal-slot="position-value">{displayPercent}</span>
                      <span className="blinds-modal-classic__position-unit" data-modal-slot="position-unit">%</span>
                    </div>
                  </div>
                </div>

                {/* Slider */}
                <div
                  className="blinds-modal-classic__slider-wrap"
                  style={{ '--blinds-thumb-pct': `${displayPercent}%` } as React.CSSProperties}
                >
                  <div
                    className="blinds-modal-classic__slider-track"
                    data-modal-slot="position-slider"
                  >
                    <div
                      className="blinds-modal-classic__slider-fill"
                      data-modal-slot="position-slider-fill"
                      style={{ width: `${displayPercent}%` }}
                    />
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={displayPercent}
                    className="blinds-modal-classic__slider-input"
                    aria-label="Blinds position"
                    onChange={handleSliderChange}
                    onPointerUp={handleSliderPointerUp}
                  />
                </div>
              </div>
            )
          case 'presets':
            return (
              <div key="presets" className={`blinds-modal-classic__presets-section${hl}`} data-modal-section="presets">
                <div className="blinds-modal-classic__presets-label" data-modal-slot="presets-label">PRESETS</div>
                <div className="blinds-modal-classic__presets-grid">
                  {PRESETS.map((preset) => {
                    const isActive = activePreset === preset.id
                    return (
                      <button
                        key={preset.id}
                        className={`blinds-modal-classic__preset-btn${isActive ? ' active' : ''}`}
                        data-modal-slot="preset-btn"
                        data-modal-slot-state={isActive ? 'active' : 'default'}
                        onClick={() => setPosition(preset.value)}
                        aria-label={preset.label}
                      >
                        <span
                          className="material-symbols-rounded"
                          data-modal-slot="preset-btn-icon"
                          data-modal-slot-state={isActive ? 'active' : 'default'}
                        >
                          {preset.icon}
                        </span>
                        <span
                          data-modal-slot="preset-btn-text"
                          data-modal-slot-state={isActive ? 'active' : 'default'}
                        >
                          {preset.label}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          default:
            return null
        }
      })}
    </div>
  )
}
